// #1115B S9 — Project dashboard banner for unconfigured GitHub binding.
//
// Mirrors GateStatusBanner.tsx's visual pattern (non-dismissible while the
// condition holds; same dashboard-top mount point). Operator-refined copy
// per Q4: the "for this project" qualifier disambiguates from tenant-level
// GitHub Token state (a user can have tenant-level GitHub connected but
// lack a project-scoped repo binding).
//
// V4 symmetry: banner trigger condition is `binding === null || binding.state
// !== 'PROVISIONED'`. This mirrors the gate-item's PASS condition (an
// infrastructure_state row with status='PROVISIONED' exists). Both surfaces
// read the SAME projection of the SAME data via GET /projects/:id's
// githubBinding denormalisation — operator V4 check verifies this.

import { Link } from 'react-router-dom';

export type GitHubBindingState = 'PROVISIONED' | 'SKIPPED' | 'FAILED' | 'PENDING';

export interface GitHubBinding {
  state:    GitHubBindingState;
  metadata?: {
    url?:           string;
    fullName?:      string;
    ownerLogin?:    string;
    defaultBranch?: string;
  };
}

interface Props {
  projectId: string;
  /** From GET /projects/:id's githubBinding denorm. null = no row exists. */
  binding:   GitHubBinding | null;
}

export default function GitHubSetupBanner({ projectId, binding }: Props) {
  // V4 — banner shows when condition is the inverse of the gate's PASS.
  const shouldShow = binding === null || binding.state !== 'PROVISIONED';
  if (!shouldShow) return null;

  return (
    <div
      role="alert"
      data-testid="github-setup-banner"
      style={{
        display:       'flex',
        alignItems:    'center',
        justifyContent: 'space-between',
        gap:           12,
        padding:       '10px 14px',
        background:    'rgba(245, 158, 11, 0.08)',
        border:        '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius:  'var(--radius-sm)',
        fontFamily:    'var(--font-body)',
        fontSize:      13,
        color:         'rgb(180, 83, 9)',
      }}
    >
      <div style={{ flex: 1, lineHeight: 1.5 }}>
        <strong>GitHub repository not configured for this project.</strong>{' '}
        The Development phase needs a connected repo before code generation can begin.
      </div>
      <Link
        to={`/projects/${projectId}/settings/github`}
        data-testid="github-setup-banner-cta"
        style={{
          flexShrink:    0,
          padding:       '6px 14px',
          fontFamily:    'var(--font-display)',
          fontSize:      12,
          fontWeight:    600,
          color:         'rgb(180, 83, 9)',
          background:    'transparent',
          border:        '1px solid rgb(245, 158, 11)',
          borderRadius:  12,
          textDecoration: 'none',
          whiteSpace:    'nowrap',
        }}
      >
        Configure GitHub →
      </Link>
    </div>
  );
}
